'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { supabase } from '@/lib/supabase';
import type { Session } from '@supabase/supabase-js';

// Tipagem dos dados (Types)
interface User {
    name: string;
    email: string;
    avatarUrl: string;
}

interface MarketData {
    value: string;
    pctChange: string;
    min: string;
    max: string;
    lastUpdate: string;
}

interface DashboardContextType {
    // Estado de Navegação
    currentPath: string;
    setCurrentPath: (path: string) => void;
    activeSubMenu: string;
    setActiveSubMenu: (menu: string) => void;

    // Sessão e Autenticação (fonte única de verdade)
    session: Session | null;
    isSessionLoading: boolean;
    isAuthenticated: boolean;

    // Estado do Usuário
    user: User | null;
    logout: () => void;

    // Estado de Negócios (Cotações)
    currencyData: MarketData;
    coffeeData: MarketData;
    updateMarketData: () => void;

    // Atualizar usuário
    refreshUser: () => Promise<void>;

    // UI
    isDarkMode: boolean;
    toggleTheme: () => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

const ROUTE_META: Record<string, { id: string; label: string }> = {
    '/': { id: 'dashboard', label: 'Dashboard' },
    '/home': { id: 'dashboard', label: 'Dashboard' },
    '/dashboard': { id: 'dashboard', label: 'Dashboard' },
    '/contratos': { id: 'contratos', label: 'Manutenção de Contratos' },
    '/cadastros/importadores': { id: 'cadastrarImportador', label: 'Cadastro de Compradores' },
    '/cadastros/exportadores': { id: 'cadastrarExportador', label: 'Cadastro de Vendedores/Entregadores' },
    '/cadastros/produtos': { id: 'cadastrarProduto', label: 'Cadastro de Produtos' },
    '/cadastros/usuarios': { id: 'cadastrarUsuario', label: 'Cadastrar Novo Usuário' },
    '/perfil': { id: 'perfilUsuario', label: 'Perfil do Usuário' },
    '/comissoes': { id: 'comissoes', label: 'Gerenciamento de Comissões' },
};

export function DashboardProvider({ children }: { children: ReactNode }) {
    const router = useRouter();
    const pathname = usePathname();
    const routeMeta = ROUTE_META[pathname] || null;
    
    // Estado Inicial (Defaults do FFAppState)
    const [currentPath, setCurrentPath] = useState(routeMeta?.label || 'Dashboard');
    const [activeSubMenu, setActiveSubMenu] = useState(routeMeta?.id || 'dashboard');
    const [isDarkMode, setIsDarkMode] = useState(() => {
        if (typeof window === 'undefined') return false;
        const storedTheme = window.localStorage.getItem('theme');
        if (storedTheme === 'dark') return true;
        if (storedTheme === 'light') return false;
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    // --- Sessão centralizada ---
    const [session, setSession] = useState<Session | null>(null);
    const [isSessionLoading, setIsSessionLoading] = useState(true);
    const profileLoadCounterRef = useRef(0);

    const [user, setUser] = useState<User | null>(null);

    const [currencyData, setCurrencyData] = useState<MarketData>({
        value: '6.000',
        pctChange: '0.555',
        min: '4.900',
        max: '5.100',
        lastUpdate: '10:00',
    });

    const [coffeeData, setCoffeeData] = useState<MarketData>({
        value: '250.000',
        pctChange: '1.200',
        min: '240.000',
        max: '260.000',
        lastUpdate: '10:00',
    });

    useEffect(() => {
        const meta = ROUTE_META[pathname];
        if (!meta) return;
        setCurrentPath(meta.label);
        setActiveSubMenu(meta.id);
    }, [pathname]);

    // Função reutilizável para buscar perfil do usuário
    const DEFAULT_AVATAR_URL = 'https://prurnebjylixteaxaepa.supabase.co/storage/v1/object/public/coffeeSelection/fotoPerfil/avatar-do-usuario.png';

    const fetchUserProfile = useCallback(async (userId: string, userEmail?: string): Promise<User> => {
        const email = userEmail || '';
        try {
            const { data, error } = await supabase
                .from('users')
                .select('nome, fotoPerfil')
                .eq('user_id', userId)
                .single();

            if (error || !data) {
                return {
                    name: email?.split('@')[0] || 'Usuário',
                    email,
                    avatarUrl: '',
                };
            }

            return {
                name: data.nome || email?.split('@')[0] || 'Usuário',
                email,
                avatarUrl: data.fotoPerfil && data.fotoPerfil !== DEFAULT_AVATAR_URL ? data.fotoPerfil : '',
            };
        } catch {
            return {
                name: email?.split('@')[0] || 'Usuário',
                email,
                avatarUrl: '',
            };
        }
    }, []);

    // refreshUser: recarrega perfil a partir da sessão atual
    const refreshUser = useCallback(async () => {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.user) {
            const profile = await fetchUserProfile(currentSession.user.id, currentSession.user.email);
            setUser(profile);
        }
    }, [fetchUserProfile]);

    // --- Inicialização e listener de autenticação (UMA vez, sem dependência de pathname) ---
    // Ref para evitar que initSession() e o listener disputem quem define a sessão (race condition)
    const sessionInitializedRef = useRef(false);

    useEffect(() => {
        let isMounted = true;

        const syncUserFromSession = (nextSession: Session | null) => {
            setSession(nextSession);

            if (!nextSession?.user) {
                setUser(null);
                return;
            }

            // Mantém apenas o último carregamento de perfil válido.
            const requestId = ++profileLoadCounterRef.current;

            void (async () => {
                const profile = await fetchUserProfile(nextSession.user.id, nextSession.user.email);
                if (!isMounted || requestId !== profileLoadCounterRef.current) return;
                setUser(profile);
            })();
        };

        // Função para inicialização imediata (antes do listener)
        const initSession = async () => {
            try {
                // Tenta pegar a sessão atual de forma rápida
                const { data: { session: initialSession } } = await supabase.auth.getSession();
                
                if (!isMounted) return;

                syncUserFromSession(initialSession);
            } catch (err) {
                console.error('Erro na inicialização da sessão:', err);
            } finally {
                if (isMounted && !sessionInitializedRef.current) {
                    sessionInitializedRef.current = true;
                    setIsSessionLoading(false);
                }
            }
        };

        initSession();

        // Listener ÚNICO — recebe INITIAL_SESSION logo na subscrição, além de
        // SIGNED_IN, SIGNED_OUT e TOKEN_REFRESHED.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
            if (!isMounted) return;

            if (!sessionInitializedRef.current) {
                sessionInitializedRef.current = true;
                setIsSessionLoading(false);
            }

            switch (event) {
                case 'SIGNED_OUT':
                    syncUserFromSession(null);
                    return;
                case 'INITIAL_SESSION':
                case 'SIGNED_IN':
                case 'TOKEN_REFRESHED':
                case 'USER_UPDATED':
                    syncUserFromSession(newSession);
                    return;
                default:
                    // Para eventos não críticos, preserva sessão sem bloquear.
                    setSession(newSession);
                    return;
            }
        });

        // Fallback: se o listener não disparar INITIAL_SESSION em 3s
        // (ex.: supabase offline), liberar o loading para AuthGuard redirecionar.
        const fallbackTimer = setTimeout(() => {
            if (isMounted && !sessionInitializedRef.current) {
                sessionInitializedRef.current = true;
                setIsSessionLoading(false);
            }
        }, 3000);

        return () => {
            isMounted = false;
            clearTimeout(fallbackTimer);
            subscription.unsubscribe();
        };
    }, [fetchUserProfile]);

    // Ação de logout
    const logout = useCallback(async () => {
        try {
            await Promise.race([
                supabase.auth.signOut({ scope: 'local' }),
                new Promise((resolve) => setTimeout(resolve, 5000)),
            ]);
        } catch {
            // Ignora erro remoto e segue com limpeza local
        } finally {
            setSession(null);
            setUser(null);

            if (typeof window !== 'undefined') {
                window.localStorage.removeItem('coffee-selection-auth');
                window.sessionStorage.removeItem('coffee-selection-auth');
            }

            router.replace('/login');
            router.refresh();

            if (typeof window !== 'undefined') {
                setTimeout(() => {
                    if (window.location.pathname !== '/login') {
                        window.location.assign('/login');
                    }
                }, 250);
            }
        }
    }, [router]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        document.documentElement.classList.toggle('dark', isDarkMode);
        window.localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    const toggleTheme = () => setIsDarkMode((prev) => !prev);

    // Ação simulada de atualizar dados (Action Block)
    const updateMarketData = useCallback(() => {
        const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        setCurrencyData(prev => ({ ...prev, lastUpdate: now, value: (Math.random() * (5.2 - 4.8) + 4.8).toFixed(2) }));
        setCoffeeData(prev => ({ ...prev, lastUpdate: now }));
    }, []);

    const isAuthenticated = !!session?.user;

    return (
        <DashboardContext.Provider value={{
            currentPath, setCurrentPath,
            activeSubMenu, setActiveSubMenu,
            session, isSessionLoading, isAuthenticated,
            user, logout,
            refreshUser,
            currencyData, coffeeData, updateMarketData,
            isDarkMode, toggleTheme
        }}>
            <div className={isDarkMode ? 'dark' : ''}>
                {children}
            </div>
        </DashboardContext.Provider>
    );
}

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) throw new Error('useDashboard must be used within a DashboardProvider');
    return context;
};
